import * as Sentry from '@sentry/browser';
import { useAuthApi } from '@store/api/authApi';
import { useUserStore } from '@/store/user';
import type { GtmLoginMethod } from '@composables/gtm';

// URL-fragment key used by the marketing landing to deliver the one-time
// cross-domain handoff code: `https://step-vpn.cloud/...#handoff=<code>`.
const HANDOFF_PARAM = 'handoff';

// In-flight handoff exchange, registered by main.ts before the router mounts.
// The router's initial-navigation guard awaits it so that redirect/promo
// handling for authed users (`token && areAuthRoutes`) sees the freshly
// established session instead of racing ahead of the async exchange.
let pendingHandoff: Promise<boolean> | null = null;

export const setPendingHandoff = (p: Promise<boolean> | null): void => {
	pendingHandoff = p;
};

/**
 * Block until the in-flight handoff exchange (if any) settles. Resolves
 * immediately once consumed, so it adds no cost to later navigations.
 */
export const awaitPendingHandoff = async (): Promise<void> => {
	if (!pendingHandoff) return;
	try {
		await pendingHandoff;
	} catch {
		// consumeHandoffCode never rejects, but stay defensive.
	}
	pendingHandoff = null;
};

/**
 * Synchronously detect a cross-domain handoff code in the URL fragment
 * (`#handoff=<code>`) and IMMEDIATELY strip it from the URL via
 * `history.replaceState`, so the single-use code never lingers in browser
 * history, server/access logs, the `Referer` header, or analytics.
 *
 * Pure and dependency-free: touches only `window.location` / `history`, so it
 * can (and must) run at the very top of app startup — before any analytics is
 * initialised. Returns the captured code, or `null` if none is present.
 *
 * Any other fragment params are preserved; only `handoff` is removed.
 */
export const captureHandoffCode = (): string | null => {
	try {
		const hash = window.location.hash; // e.g. "#handoff=abc123"
		if (!hash || hash.length < 2) return null;

		const params = new URLSearchParams(hash.slice(1));
		const code = params.get(HANDOFF_PARAM);
		if (!code) return null;

		// Strip the code from the URL before anything else can observe it.
		params.delete(HANDOFF_PARAM);
		const rest = params.toString();
		const cleanUrl =
			window.location.pathname +
			window.location.search +
			(rest ? `#${rest}` : '');

		history.replaceState(history.state, document.title, cleanUrl);
		return code;
	} catch (e) {
		// URL parsing must never break app startup.
		console.warn('[handoff] failed to read fragment', e);
		return null;
	}
};

/**
 * True on the OAuth return routes `/login/<provider>` and `/welcome/<provider>`.
 *
 * There the code must be exchanged by the page, not by app startup: the page
 * knows which provider it came back from and reports that as the login method,
 * and the code is single-use — whoever spends it first wins. Startup keeps
 * handling every other entry (the landing's OTP handoff), where 'email' is the
 * right method.
 */
export const isOauthCallbackPath = (): boolean => {
	try {
		return /^\/(login|welcome)\/[^/]+/.test(window.location.pathname);
	} catch {
		return false;
	}
};

/**
 * Read the failure code from an unsuccessful OAuth return.
 *
 * A successful return carries nothing but `#handoff=<code>` — no query at all.
 * A failed one comes back as `?success=false&message=…&code=…`, and that is the
 * only reason to look at the query here.
 *
 * Reads and nothing else. These parameters are how the backend talks to the
 * front end, and other pages depend on finding them: connect.vue parses
 * `location.search` itself to learn whether linking a social account worked.
 * Stripping them here — from a module that must run before everything else —
 * silently emptied the address under it. They also no longer hold anything
 * secret: the session token was removed from the OAuth redirect entirely, so
 * there is nothing left to hide from history or a `Referer`.
 *
 * Returns the backend's error code (a key under `errorCode.*` in the locale
 * files), or null — either because nothing failed, or because the failure came
 * without a code (the backend only appends one when the error has it). Callers
 * substitute their own default anyway: they also have to cover the case where
 * the page was opened with no parameters at all.
 */
export const captureAuthErrorCode = (): string | null => {
	try {
		const params = new URLSearchParams(window.location.search);
		if (params.get('success') !== 'false') return null;

		return params.get('code');
	} catch (e) {
		console.warn('[auth] failed to read the OAuth result', e);
		return null;
	}
};

/**
 * Exchange a previously-captured handoff code for a real session on this
 * domain (`POST /auth/handoff/exchange`).
 *
 * Security:
 * - Does NOT silently overwrite an active session (login-CSRF mitigation): if a
 *   token already exists, the incoming code is ignored and the current session
 *   is preserved.
 * - On ANY exchange failure (expired / already-used / network) it resolves to
 *   `false` so the caller falls through to the normal login flow instead of
 *   crashing.
 *
 * Returns `true` only when a fresh session was established from the code.
 */
export const consumeHandoffCode = async (
	code: string,
	method: GtmLoginMethod = 'email',
): Promise<boolean> => {
	const authApi = useAuthApi();

	// Never clobber an existing session — mitigates login-CSRF (an attacker
	// forwarding their own handoff link to a logged-in victim).
	if (authApi.methods.getToken()) {
		Sentry.addBreadcrumb({
			category: 'auth.handoff',
			level: 'warning',
			message: 'Handoff code ignored: an active session already exists',
		});
		console.warn('[handoff] ignored — already authenticated');
		return false;
	}

	try {
		const { user, is_new_user } = await authApi.methods.loginWithHandoff(code);

		// Token is persisted by loginWithHandoff. Mirror the OTP/social login
		// setup (login/sign_up + Sentry user) using the user returned by the
		// exchange, so the fresh login is reflected in analytics immediately.
		// `method` says which flow minted the code: the landing's email-OTP
		// (default) or an OAuth return handled by the login page.
		const userStore = useUserStore();
		await userStore.methods.setup(user, method, is_new_user);

		Sentry.addBreadcrumb({
			category: 'auth.handoff',
			level: 'info',
			message: 'Handoff exchange succeeded',
		});
		return true;
	} catch (e: any) {
		// `handoff_invalid` (expired / already-used / unknown), `origin_not_allowed`
		// (misconfigured origin), or a network error → fall back to normal login.
		const code = e?.code as string | undefined;
		console.warn(`[handoff] exchange failed (${code ?? 'network'}), falling back to login`, e);
		Sentry.addBreadcrumb({
			category: 'auth.handoff',
			level: 'warning',
			message: 'Handoff exchange failed; falling back to login',
			data: { code: code ?? null, error: e?.message ?? String(e) },
		});
		return false;
	}
};
